/**
 * ProcessDesigner — 4-step wizard for process definition lifecycle.
 *
 * Step 1 Basics    — name, description, and WHO MAY SUBMIT (initiator_role_ids)
 * Step 2 Form      — the request form the worker fills in
 * Step 3 Workflow  — the approval path AND each step's settings: click a node, configure it in
 *                    the right panel (approver, backup, target time, routing, condition,
 *                    message). This is the ONLY place node config lives.
 * Step 4 Review    — summary + requester-view preview + approver preflight + publish
 *
 * There used to be a fifth step ("Approvals" / "Execution Rules") rendering
 * `ExecutionRulesEditor`, which was a second UI over the SAME `definition.flow` nodes that
 * step 3's right panel already edits through the shared `ExecutionRuleFields`. Two editors for
 * one contract is the drift pattern (svdy-rules §10.5b) and the owner read it as exactly that
 * (2026-08-23: "为什么把第4步改成了现在应该可以在第三步就能直观配置的能力").
 *
 * Policy records are NOT a wizard step: the engine does not consume them, so a step that
 * "configures policies" would change nothing at runtime. They stay an administrative catalog
 * under Process Policies, and Review says so.
 *
 * Entry point is a definitions list; clicking a card enters the wizard.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Step,
  StepButton,
  StepConnector,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  AccountTree as FlowIcon,
  ArchiveOutlined as ArchiveIcon,
  ContentCopyOutlined as VersionIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  CheckCircleOutlined,
  CancelOutlined,
  ErrorOutlineOutlined,
} from '@mui/icons-material';

import { ApiError, processesApi } from '../../../api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DESIGNER_STAGE_HEIGHT } from '../../../theme/shellMetrics';
import { apiErrorCode, translateApiError } from '../../../i18n/translateApiError';
import { type FlowIssue, flowIssueMessage, flowIssuesFrom } from './flowIssues';
import type {
  ProcessDefinition,
  ProcessDefinitionCreate,
  ProcessDefinitionPreflight,
  ProcessRole,
} from '../../../api/processes';
import { ConfirmDialog } from '../../../components/primitives';
import ProcessFormPreview from '../../../components/process/ProcessFormPreview';
import type { PreviewFrame, ProcessFormPreviewProps } from '../../../components/process/ProcessFormPreview';
import FlowEditor from './FlowEditor';
import ProcessFormDesign from './components/WizardFormBuilder';
import TemplateCatalogDialog from './components/TemplateCatalogDialog';
import type { FormComponent, FormFlowStep } from './components/WizardFormBuilder';
import { getProcessDefinitionDisplay, getProcessDefinitionFieldLabel, getProcessDefinitionFieldOptions, getProcessDefinitionNodeLabel, systemDefinitionKey, translateFormComponentCode, translateProcessStatus } from '../processDisplay';
import type { ProcessTranslator } from '../processDisplay';
import {
  hasTaskAttachmentConfiguration,
  restoreFormSteps,
  serializeFormSteps,
} from './formSchemaContract';
import type { PersistedFormField, PersistedFormSchema } from './formSchemaContract';
import { WIZARD_STEP_SHAFT_WIDTH, wizardStepArrowStyle } from './wizardStepConnector';

type Mode = { kind: 'list' } | { kind: 'wizard'; defId: string };

const STEPS_KEYS = [
  'processes:processDesigner.steps.basicInfo',
  'processes:processDesigner.steps.formDesign',
  'processes:processDesigner.steps.flowDesign',
  'processes:processDesigner.steps.publish',
];

function localizeDefinitionFormSteps(
  t: ProcessTranslator,
  definition: ProcessDefinition,
  steps: FormFlowStep[],
): FormFlowStep[] {
  const schema = definition.form_schema as PersistedFormSchema | null;
  const fields = schema && Array.isArray(schema.fields) ? schema.fields : [];
  const fieldsById = new Map<string, PersistedFormField>(
    fields.filter((field) => !!field.id).map((field) => [field.id, field]),
  );
  const trustedSystemDefinition = !!systemDefinitionKey(definition);

  return steps.map((step, index) => {
    const legacyField = step.id === `step-restored-${index}` ? fields[index] : undefined;
    const source = fieldsById.get(step.id) ?? legacyField;
    return {
      ...step,
      component: {
        ...step.component,
        displayName: translateFormComponentCode(
          t,
          step.component.fieldType ?? step.component.name,
        ),
      },
      display: trustedSystemDefinition && source
        ? {
          label: getProcessDefinitionFieldLabel(t, definition, source, index),
          options: getProcessDefinitionFieldOptions(t, definition, source)
            .map((option) => option.label),
        }
        : undefined,
    };
  });
}

/**
 * The height of a process card's header row, shared by the row itself and by the delete button
 * that sits over it.
 *
 * They were positioned by two unrelated mechanisms — the status chip by `CardContent`'s `p: 2`
 * plus the row's natural height (centre ≈ 27.5px from the card top), the delete by
 * `position: absolute; top: 4` plus its own 30px box (centre ≈ 19px). About 8.5px apart, side by
 * side in the same corner, which the eye reads as a stagger because it pairs them.
 *
 * Tuning `top` to match would be a magic number that breaks the first time the title font or the
 * chip height changes. One constant that both read cannot drift: the row is `minHeight:
 * CARD_HEADER_ROW_H`, the button is `top: CARD_CONTENT_PAD` with the same height, so both centres
 * land on `CARD_CONTENT_PAD + CARD_HEADER_ROW_H / 2`.
 */
const CARD_HEADER_ROW_H = 30;
/** `CardContent sx={{ p: 2 }}` in MUI's 8px spacing = 16px. The delete overlay must start where
 *  the content starts, or it cannot share the header row's centre line. */
const CARD_CONTENT_PAD = 16;

function WizardStepConnector() {
  return (
    <StepConnector
      data-testid="wizard-step-connector"
      sx={{
        mx: 0.75,
        '& .MuiStepConnector-line': {
          position: 'relative',
          color: 'text.disabled',
          borderColor: 'currentColor',
          borderTopWidth: WIZARD_STEP_SHAFT_WIDTH,
          minWidth: 28,
          borderRadius: 1,
          '&::after': wizardStepArrowStyle(),
        },
        '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
          borderColor: 'primary.main',
          color: 'primary.main',
        },
      }}
    />
  );
}

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning'> = {
  draft: 'default', active: 'success', deprecated: 'warning', archived: 'default',
};

// ===========================================================================
// Wizard
// ===========================================================================

// ===========================================================================
// Wizard
// ===========================================================================

function DesignerWizard({ definitionId, onBack, onCreateVersion, definitions }: {
  definitionId: string;
  onBack: () => void;
  onCreateVersion: (definition: ProcessDefinition) => Promise<void>;
  definitions: ProcessDefinition[];
}) {
  const { t, i18n } = useTranslation();
  const displayLanguage = i18n.resolvedLanguage ?? i18n.language;
  const theme = useTheme();
  const [definition, setDefinition] = useState<ProcessDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [dirty, setDirty] = useState(false);
  /**
   * The tenant's OWN processes. `definitions` includes the catalog rows, which are
   * `is_template=true` definitions in the same table — 11 seeded specs plus, on any org created
   * before 2026-08-24, a generation of unmaintained `(Template)` orphans nobody ever removed.
   *
   * The list page 900 lines below already draws this distinction and says why ("Catalog templates
   * are available starters, not tenant configuration"); the wizard's duplicate-name check read the
   * unfiltered prop, so naming a draft `Leave Request` collided with the seeded template of that
   * name. Two sources for one concept, one of them stale (svdy-rules §10.5b).
   */
  const tenantDefinitions = useMemo(
    () => definitions.filter((d) => !(d as { is_template?: boolean }).is_template),
    [definitions],
  );
  /** Where the user asked to go while there were unsaved edits, held for the discard dialog. */
  const [pendingLeave, setPendingLeave] = useState<number | 'back' | null>(null);
  const [saving, setSaving] = useState(false);
  /** Why the last publish attempt was refused, per problem.
   *
   * A LIST and PERSISTENT, both deliberate. The backend returns every issue it found with a code
   * and its context; the toast that used to be the only signal said "Fix the highlighted setup"
   * — a highlight nobody drew — and then disappeared. The owner's actual refusal was ONE issue
   * naming a condition step and the form field it wanted, which is the whole difference between
   * an actionable refusal and a dead end. */
  const [publishIssues, setPublishIssues] = useState<FlowIssue[]>([]);
  const [cloning, setCloning] = useState(false);
  const [toast, setToastState] = useState<{ id: number; message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const toastSeq = useRef(0);
  /** Stamps every message with an id so the Snackbar below can key on it. */
  const setToast = useCallback(
    (next: { message: string; severity: 'success' | 'error' | 'info' } | null) => {
      toastSeq.current += 1;
      setToastState(next ? { ...next, id: toastSeq.current } : null);
    },
    [],
  );
  /** Closes only the message that asked to close, so a stale timer cannot eat a newer one. */
  const dismissToast = useCallback((id: number | undefined) => {
    setToastState((current) => (current && current.id === id ? null : current));
  }, []);
  const [preflight, setPreflight] = useState<ProcessDefinitionPreflight | null>(null);
  const [preflighting, setPreflighting] = useState(false);
  const [previewFrame, setPreviewFrame] = useState<PreviewFrame>('desktop');

  // Step 1 local state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [processRoles, setProcessRoles] = useState<ProcessRole[]>([]);
  const [initiatorRoleIds, setInitiatorRoleIds] = useState<string[]>([]);

  // Step 2 local state (form builder)
  const [formComponents, setFormComponents] = useState<FormFlowStep[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

  const fetchDef = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await processesApi.processDefinitionsApi.get(definitionId);
      setDefinition(d);
      setActiveStep(0);
      setDirty(false);
      setPreflight(null);
      setSelectedFormId(null);
      setName(d.name);
      setDescription(d.description ?? '');
      setInitiatorRoleIds(d.initiator_role_ids ?? []);
      // Restore raw persisted values once. Locale-specific display metadata is
      // derived by the separate language effect below and never triggers a refetch.
      const formSchema = (d as any).form_schema;
      setFormComponents(restoreFormSteps(formSchema, (fieldType) => fieldType));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [definitionId]);

  useEffect(() => { fetchDef(); }, [fetchDef]);

  useEffect(() => {
    processesApi.processRolesApi.list()
      .then((roles) => setProcessRoles(roles.filter((role) => role.is_active)))
      .catch(() => setProcessRoles([]));
  }, []);

  useEffect(() => {
    if (!definition) return;
    const displayT = i18n.getFixedT(displayLanguage);
    setFormComponents((current) => localizeDefinitionFormSteps(displayT, definition, current));
  }, [definition, displayLanguage, i18n]);

  const markDirty = useCallback(() => {
    if (definition?.status !== 'draft') return;
    setDirty(true);
    setPreflight(null);
  }, [definition?.status]);

  useEffect(() => {
    if (!dirty) return;
    const blockUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', blockUnload);
    return () => window.removeEventListener('beforeunload', blockUnload);
  }, [dirty]);

  // Step 2 handlers
  const addFormComponent = (component: FormComponent) => {
    markDirty();
    const newStep: FormFlowStep = {
      id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: 'form',
      component: { ...component },
      position: { x: 0, y: formComponents.length * 80 },
      properties: { ...component.properties },
      connections: [],
    };

    setFormComponents((prev) => [...prev, newStep]);
  };
  const removeFormComponent = (id: string) => {
    markDirty();
    setFormComponents((prev) => prev.filter((c) => c.id !== id));
    if (selectedFormId === id) setSelectedFormId(null);
  };
  // Whole-group delete: a field inserted from a template has no delete of its own
  // (owner 2026-08-27 — 整组删除), so the canvas withdraws every member at once.
  const removeFormComponents = (ids: string[]) => {
    if (ids.length === 0) return;
    markDirty();
    const doomed = new Set(ids);
    setFormComponents((prev) => prev.filter((c) => !doomed.has(c.id)));
    if (selectedFormId && doomed.has(selectedFormId)) setSelectedFormId(null);
  };
  // The builder computes the whole next order because a GROUP move is not a
  // single-element splice; `onDragEnd`'s old arithmetic would have torn a group apart.
  const reorderFormComponents = (nextOrder: string[]) => {
    markDirty();
    setFormComponents((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      const next = nextOrder
        .map((id) => byId.get(id))
        .filter((c): c is FormFlowStep => c !== undefined);
      // Defensive: never silently drop a step because an id did not round-trip.
      return next.length === prev.length ? next : prev;
    });
  };
  const updateFormComponent = (id: string, key: string, value: any) => {
    markDirty();
    setFormComponents((prev) =>
      prev.map((c) => c.id === id ? { ...c, properties: { ...c.properties, [key]: value } } : c)
    );
  };

  const handleSave = async () => {
    if (!definition || definition.status !== 'draft' || !dirty || activeStep > 1) return;
    setSaving(true);
    try {
      let updated: ProcessDefinition;
      if (activeStep === 0) {
        const trimmed = name.trim();
        // CUSTOMER definitions, not every row. The catalog templates are `is_template=true`
        // definitions living in the same table, and this file already says so 800 lines below
        // (`customerDefinitions`, "Catalog templates are available starters, not tenant
        // configuration") — but the duplicate check read the unfiltered list, so naming a draft
        // `Leave Request` collided with the SEEDED `Leave Request` template. One concept, two
        // sources, one stale (svdy-rules §10.5b).
        //
        // Reported by the owner as "当前组织中没有该 process 的名称，但显示已存在", which was exactly
        // right: no process of theirs had that name.
        const duplicate = tenantDefinitions.find((item) =>
          item.id !== definitionId && item.name.toLowerCase() === trimmed.toLowerCase()
        );
        if (duplicate) {
          setNameError(t('processes:processDesigner.wizard.nameExists'));
          return;
        }
        setNameError('');
        updated = await processesApi.processDefinitionsApi.update(definitionId, {
          name: trimmed,
          description: description.trim() || null,
          initiator_role_ids: initiatorRoleIds,
        });
      } else {
        updated = await processesApi.processDefinitionsApi.update(definitionId, {
          form_schema: serializeFormSteps(formComponents),
        });
      }
      setDefinition(updated);
      setDirty(false);
      setToast({ message: t('processes:processDesigner.wizard.saved'), severity: 'success' });
    } catch (caught) {
      setToast({
        message: caught instanceof ApiError
          ? translateApiError(caught, t)
          : t('processes:processDesigner.wizard.saveFailed'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!definition || definition.status !== 'active') return;
    setCloning(true);
    try {
      await onCreateVersion(definition);
    } finally {
      setCloning(false);
    }
  };

  /** Capture a flow refusal's issue list, whichever action provoked it.
   *
   * BOTH ACTIONS PROVOKE IT (owner, 2026-09-01): `preflight_definition` and `publish_definition`
   * each call `ensure_valid_process_flow`, so "Check approver route" returns the very same
   * `invalid_process_flow` — and its handler only raised a toast, so the button reported
   * "not ready to publish" with nothing named. That is the report *"一直是This workflow is not ready
   * to publish…没有明确的提醒"*: the preflight button, not the publish one.
   *
   * Shared rather than copied into both catch blocks, because the copy is how one of them came to
   * lack the capture in the first place (svdy-rules §10.5). Returns whether it recognised the
   * refusal, so a caller can still decide what else to do.
   */
  const captureFlowIssues = (e: unknown): boolean => {
    if (!(e instanceof ApiError)) return false;
    const raw = e.detail as { detail?: unknown } | null;
    const inner = raw && typeof raw === 'object' && 'detail' in raw ? raw.detail : raw;
    if (
      !inner
      || typeof inner !== 'object'
      || (inner as { code?: string }).code !== 'invalid_process_flow'
    ) {
      return false;
    }
    setPublishIssues(flowIssuesFrom(e.detail));
    setActiveStep((inner as { section?: string }).section === 'form' ? 1 : 2);
    return true;
  };

  const handlePreflight = async () => {
    setPreflighting(true);
    setPublishIssues([]);
    try {
      const result = await processesApi.processDefinitionsApi.preflight(definitionId);
      setPreflight(result);
      setPublishIssues([]);
      setToast({
        message: t('processes:processDesigner.wizard.publish.preflightReady'),
        severity: 'success',
      });
    } catch (e) {
      setPreflight(null);
      captureFlowIssues(e);
      setToast({
        message: e instanceof ApiError
          ? translateApiError(e, t)
          : t('processes:processDesigner.wizard.publish.preflightFailed'),
        severity: 'error',
      });
    } finally {
      setPreflighting(false);
    }
  };

  // Step 5 publish
  const handlePublish = async () => {
    if (hasTaskAttachmentConfiguration(definition?.flow)) {
      setToast({
        message: t('processes:processDesigner.wizard.publish.taskAttachmentsUnsupported'),
        severity: 'error',
      });
      setActiveStep(2);
      return;
    }
    const hasApprovalStep = ((definition?.flow as { nodes?: Array<{ type?: string }> } | null)?.nodes ?? [])
      .some((node) => node.type === 'approval');
    if (hasApprovalStep && !preflight?.ready) {
      setToast({
        message: t('processes:processDesigner.wizard.publish.preflightRequired'),
        severity: 'info',
      });
      return;
    }
    setSaving(true);
    setPublishIssues([]);
    try {
      const updated = await processesApi.processDefinitionsApi.publish(definitionId);
      setDefinition(updated);
      setPublishIssues([]);
      setToast({ message: t('processes:processDesigner.wizard.published'), severity: 'success' });
    } catch (e) {
      // KEEP the issues, and jump to the offending step. Both via the shared helper, because the
      // preflight button hits the same refusal and had no capture at all.
      captureFlowIssues(e);
      setToast({ message: e instanceof ApiError ? translateApiError(e, t) : t('processes:processDesigner.wizard.publishFailed'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyTestRequestLink = async () => {
    // ABSOLUTE, and the only place this link is built. There used to be a second expression for
    // the on-screen field, which produced a RELATIVE path — so the value shown and the value copied
    // disagreed, and the visible one was unusable. The field is gone; this is the single source.
    const link = workerTestLink(definitionId);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(link);
      setToast({
        message: t('processes:processDesigner.wizard.publish.testRequestCopied'),
        severity: 'success',
      });
    } catch {
      setToast({
        message: t('processes:processDesigner.wizard.publish.testRequestCopyFailed'),
        severity: 'error',
      });
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error || !definition) {
    const message = error instanceof ApiError
      ? translateApiError(error, t)
      : error
        ? t('processes:processDesigner.loadError')
        : t('common:feedback.notFound');
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={onBack}>{t('common:actions.back')}</Button>}>
          {message}
        </Alert>
      </Box>
    );
  }

  const readOnly = definition.status !== 'draft';
  const requiresApproverPreflight = (
    (definition.flow as { nodes?: Array<{ type?: string }> } | null)?.nodes ?? []
  ).some((node) => node.type === 'approval');
  const definitionDisplay = getProcessDefinitionDisplay(t, definition);

  /**
   * One guard for EVERY way of leaving a step — the numbered chips, Back, Next and the
   * header's back arrow.
   *
   * Previously each of those was `disabled={dirty || saving}`, so with unsaved edits the
   * click did nothing at all and said nothing: the owner reported it as "步骤点击的时候,
   * 如果没有保存, 要提醒?" — a control that looks live and silently refuses is worse than one
   * that explains itself. The controls stay enabled and answer.
   */
  const requestStep = (next: number | 'back') => {
    if (saving) return;
    if (dirty) {
      // ASK, do not refuse. The previous version explained the refusal instead of performing it,
      // which was an improvement on the silent `disabled` before it — but it still left NO WAY OUT:
      // the same branch caught the header's back arrow, so a draft with unsaved edits could not be
      // abandoned at all. The owner reported exactly that ("不想保存推出，无法实现").
      //
      // Discarding is safe and cheap to offer here: every step's edits are re-derivable from the
      // saved definition, which `applyDefinition` reloads.
      setPendingLeave(next);
      return;
    }
    if (next === 'back') { onBack(); return; }
    if (next < 0 || next >= STEPS_KEYS.length || next === activeStep) return;
    setActiveStep(next);
  };

  return (
    <Box sx={{ py: '12px', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header: row 1 = back + title + nav buttons; row 2 = stepper centered */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <IconButton onClick={() => requestStep('back')} size="small" disabled={saving}><BackIcon /></IconButton>
          <Typography variant="h6" fontWeight={600} noWrap sx={{ maxWidth: 280, ml: 1, overflow: 'hidden', textOverflow: 'ellipsis' }} title={definitionDisplay.name}>
            {definitionDisplay.name}
          </Typography>
          <Chip label={translateProcessStatus(t, definition.status)} size="small" color={STATUS_COLORS[definition.status] ?? 'default'} sx={{ ml: 1.5 }} />
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button
              disabled={activeStep === 0 || saving}
              onClick={() => requestStep(activeStep - 1)}
              sx={{ textTransform: 'none' }}
            >
              {t('common:actions.back')}
            </Button>
            {activeStep <= 1 && !readOnly && (
              <Button
                variant="outlined"
                size="small"
                disabled={saving || !dirty || (activeStep === 0 && !name.trim())}
                sx={{ textTransform: 'none', borderRadius: 1 }}
                onClick={handleSave}
              >
                {saving ? <CircularProgress size={14} color="inherit" /> : t('common:actions.save')}
              </Button>
            )}
            {activeStep < STEPS_KEYS.length - 1 && (
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => requestStep(activeStep + 1)}
                sx={{ textTransform: 'none', borderRadius: 1 }}
              >
                {t('common:actions.next')}
              </Button>
            )}
            {activeStep === STEPS_KEYS.length - 1 && definition.status === 'draft' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublish}
                disabled={dirty || saving || (requiresApproverPreflight && !preflight?.ready)}
                sx={{ textTransform: 'none', borderRadius: 1 }}
              >
                {saving ? <CircularProgress size={16} color="inherit" /> : t('processes:processDesigner.wizard.publishAction')}
              </Button>
            )}
          </Stack>
        </Box>
        {/* LEFT-ALIGNED, like everything else on this page (owner, 2026-09-01: 四个步骤是否居左？
          * 包括第一个basic和review的两个页面的卡片也居左…不然很割裂).
          *
          * Counted before changing anything, because "one axis" is only a rule if you know how many
          * there were: THREE. The page header sat at the gutter, the read-only Alert spanned full
          * width from the gutter, and the Form step's three columns did too — while the stepper
          * (maxWidth 760), the Basics card (720) and the Review card (800) each centred themselves
          * with `mx: 'auto'`. So the majority axis was the gutter and the centred trio were the
          * exceptions, which is what the owner read as 割裂 (svdy-ui §15 — one alignment axis per
          * page; filling space never justifies a second one).
          *
          * `maxWidth` STAYS. A 2000px-wide text field is its own defect, and the cap is why these
          * numbers exist; `mx: 'auto'` is what made a cap into a second origin. Cap, not centre. */}
        <Stepper
          activeStep={activeStep}
          connector={<WizardStepConnector />}
          // 760, not 680: at 680 every two-word label wrapped onto a second line, which is
          // what the owner's screenshot shows. Paired with nowrap below so a longer locale
          // (German "Veröffentlichen") widens the row instead of stacking.
          sx={{ maxWidth: 760 }}
        >
          {STEPS_KEYS.map((key, i) => (
            <Step key={key}>
              <StepButton
                disabled={saving}
                onClick={() => requestStep(i)}
                sx={{ '& .MuiStepLabel-label': { whiteSpace: 'nowrap' } }}
              >
                {t(key)}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Lock info for active processes with instances (backend enforces on save) */}
      {definition.status !== 'draft' && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={definition.status === 'active' ? (
            <Button
              size="small"
              startIcon={cloning ? <CircularProgress size={14} color="inherit" /> : <VersionIcon />}
              disabled={cloning}
              onClick={handleCreateVersion}
              sx={{ textTransform: 'none' }}
            >
              {t('processes:processDesigner.versioning.create')}
            </Button>
          ) : undefined}
        >
          {/* ONE sentence. `createDescription` ("a new draft will be created, the published
              process stays active…") used to be concatenated here, which made a two-line banner
              the user re-read on all four steps. It describes what the BUTTON does, so it now
              lives on the create-version confirmation instead. */}
          {definition.status === 'active'
            ? t('processes:processDesigner.versioning.readOnly')
            : t('processes:processDesigner.versioning.archivedReadOnly')}
        </Alert>
      )}

      {/* WHY THE LAST ATTEMPT WAS REFUSED — ABOVE the step content, not inside the Review panel.
        *
        * It started inside Review, which is where the publish button is. But a flow refusal sends
        * the admin to step 2 (Workflow) or step 1 (Form), because that is where the offending node
        * or field lives — so the list stayed on a panel they had just been navigated away from. The
        * owner clicked "Check approver route", landed on Workflow, and saw nothing.
        *
        * Rendered once here, so it follows every jump and survives every step. */}
      {publishIssues.length > 0 && (
        <Box sx={{ px: { xs: 0, md: 1 }, pb: 1.5 }}>
          <Alert
            severity="error"
            onClose={() => setPublishIssues([])}
            sx={{ borderRadius: 1.5 }}
          >
            <AlertTitle sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('processes:processDesigner.wizard.publish.issuesTitle', {
                count: publishIssues.length,
              })}
            </AlertTitle>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
              {publishIssues.map((issue, index) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={`${issue.code}-${issue.node_id ?? issue.field ?? index}`}
                >
                  {flowIssueMessage(issue, t)}
                </Typography>
              ))}
            </Stack>
          </Alert>
        </Box>
      )}

      {/* Step content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* Step 1: Basic Info */}
        {activeStep === 0 && (
          <Paper component="fieldset" disabled={readOnly} aria-readonly={readOnly} sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none', maxWidth: 720, minInlineSize: 0, opacity: readOnly ? 0.8 : 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>{t('processes:processDesigner.wizard.basicInfo.title')}</Typography>
            <Stack spacing={2.5}>
              <TextField label={t('processes:processDesigner.wizard.basicInfo.processName')} value={name} onChange={e => { setName(e.target.value); setNameError(''); markDirty(); }} fullWidth required error={!!nameError} helperText={nameError || undefined} />
              <TextField label={t('processes:processDesigner.wizard.basicInfo.description')} value={description} onChange={e => { setDescription(e.target.value); markDirty(); }} fullWidth multiline rows={3} />
              <FormControl fullWidth>
                <InputLabel shrink id="process-initiator-roles-label">
                  {t('processes:processDesigner.wizard.basicInfo.whoCanSubmit')}
                </InputLabel>
                <Select
                  multiple
                  notched
                  // MUI skips `renderValue` for an empty value unless `displayEmpty` is
                  // set, so without it the "no restriction" default rendered as a blank
                  // box — the one state that most needs a label, because blank reads as
                  // "unconfigured" when it actually means "everyone may submit".
                  displayEmpty
                  labelId="process-initiator-roles-label"
                  value={initiatorRoleIds}
                  label={t('processes:processDesigner.wizard.basicInfo.whoCanSubmit')}
                  onChange={(event) => {
                    setInitiatorRoleIds(event.target.value as string[]);
                    markDirty();
                  }}
                  renderValue={(selected) => (selected as string[]).length === 0
                    ? t('processes:processDesigner.wizard.basicInfo.everyone')
                    : processRoles
                      .filter((role) => (selected as string[]).includes(role.id))
                      .map((role) => role.name)
                      .join(', ')}
                >
                  {processRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {initiatorRoleIds.length === 0
                    ? t('processes:processDesigner.wizard.basicInfo.everyoneHelper')
                    : t('processes:processDesigner.wizard.basicInfo.selectedRolesHelper')}
                </Typography>
              </FormControl>
            </Stack>
          </Paper>
        )}

        {/* Step 2: Form Design — full three-column builder */}
        {activeStep === 1 && (
          <Box aria-readonly={readOnly} sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.8 : 1 }}>
            <ProcessFormDesign
              formComponents={formComponents}
              selectedId={selectedFormId}
              onSelect={setSelectedFormId}
              onAdd={addFormComponent}
              onTemplateInserted={(template, count) => setToast({
                message: t('processes:wizardForm.templateInserted', {
                  name: template.displayName ?? template.name,
                  count,
                }),
                severity: 'success',
              })}
              onEmptyTemplate={(template) => setToast({
                message: t('processes:wizardForm.templateHasNoFields', {
                  name: template.displayName ?? template.name,
                }),
                severity: 'info',
              })}
              onRemove={removeFormComponent}
              onRemoveMany={removeFormComponents}
              onReorderSteps={reorderFormComponents}
              onUpdate={updateFormComponent}
            />
          </Box>
        )}

        {/* Step 3: Flow Design */}
        {activeStep === 2 && (
          <Box sx={DESIGNER_STAGE_HEIGHT}>
            <FlowEditor
              definitionId={definition.id}
              onBack={() => {
                if (!dirty && !saving) setActiveStep(1);
              }}
              onSaved={(updated) => {
                setDefinition(updated);
                setDirty(false);
                setPreflight(null);
              }}
              onDirtyChange={(nextDirty) => {
                setDirty(nextDirty);
                if (nextDirty) setPreflight(null);
              }}
            />
          </Box>
        )}

        {/* Step 4: Review — node settings live in step 3's right panel, not in a second editor. */}
        {activeStep === 3 && (() => {
          const flow = definition.flow as { nodes?: { type?: string; data?: { label?: string } }[] } | null;
          const flowNodes = flow?.nodes ?? [];
          const hasStart = flowNodes.some(n => n.type === 'start' || n.data?.label === 'Submit');
          const hasEnd = flowNodes.some(n => n.type === 'end');
          return (
            <Paper sx={{ p: 2.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, boxShadow: 'none', maxWidth: 800 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>{t('processes:processDesigner.wizard.publish.title')}</Typography>
              {/* THE POLICY NOTE IS GONE (owner, 2026-09-01: 这句话对吗？…很直白有歧义).
                *
                * It read "Process policies are maintained separately as administrative records and
                * are not applied automatically, so there is nothing to select here" — the first
                * thing on the Review step, in an info Alert.
                *
                * Three things wrong with it, and none was the wording:
                *  1. it explained the ABSENCE OF A FEATURE THAT NO LONGER EXISTS. The whole
                *     `ProcessPolicy/` directory was deleted on 2026-08-23; nobody using this wizard
                *     has ever seen a policy selector, so nobody was waiting for one.
                *  2. it answered a question the reader had not asked, in OUR vocabulary
                *     ("administrative records"), at the top of the step where they came to publish.
                *  3. the zh-CN said 「因此这里不提供虚假的策略选择」 — "so we do not offer a fake
                *     policy choice here", which invites the reader to wonder where we do.
                *
                * An empty state should say what to do next (svdy-ui §7). This said there is nothing
                * to do, about something that is not there. Deleted rather than reworded. */}

              {/* Summary cards — CSS Grid per svdy-ui §11.1 (avoids MUI
                  Grid container negative-margin shifting stats off-center) */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 3,
              }}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="primary.main">{flowNodes.length}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('processes:processDesigner.wizard.publish.flowNodes')}</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="success.main">{formComponents.length}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('processes:processDesigner.wizard.publish.formFields')}</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} color="warning.main">v{definition.version}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('processes:processDesigner.wizard.publish.version')}</Typography>
                </Box>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <Chip label={translateProcessStatus(t, definition.status)} size="small" color={STATUS_COLORS[definition.status] ?? 'default'} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{t('processes:processDesigner.filterStatus')}</Typography>
                </Box>
              </Box>

              {/* What the requester sees — the real read-only renderer, not a mock. */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {t('processes:formPreview.title')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('processes:formPreview.subtitle')}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={previewFrame}
                  onChange={(_event, next) => { if (next) setPreviewFrame(next); }}
                  aria-label={t('processes:formPreview.title')}
                >
                  <ToggleButton value="desktop" sx={{ textTransform: 'none', px: 1.5 }}>
                    {t('processes:formPreview.desktop')}
                  </ToggleButton>
                  <ToggleButton value="phone" sx={{ textTransform: 'none', px: 1.5 }}>
                    {t('processes:formPreview.phone')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box sx={{ mb: 1 }}>
                <ProcessFormPreview
                  // One conversion at the boundary: the API types `form_schema` as an opaque
                  // JSONB record, while the preview reads the shared field contract. The
                  // component guards the shape at runtime (`Array.isArray(schema?.fields)`).
                  schema={definition.form_schema as ProcessFormPreviewProps['schema']}
                  currency={(definition as { organization_currency?: string }).organization_currency}
                  frame={previewFrame}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
                {t('processes:formPreview.mobileNote')}
              </Typography>

              {/* Flow preview */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('processes:processDesigner.wizard.publish.flowPreview')}</Typography>              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', mb: 2 }}>
                {flowNodes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('processes:processDesigner.wizard.publish.noFlowNodes')}</Typography>
                ) : (() => {
                  // Build a simple tree from edges for visual hierarchy
                  const edges = (flow as any)?.edges ?? [];
                  const childrenOf = new Map<string, string[]>();
                  edges.forEach((e: any) => {
                    const list = childrenOf.get(e.source) ?? [];
                    list.push(e.target);
                    childrenOf.set(e.source, list);
                  });
                  // Find start node
                  const startNode = flowNodes.find(n => n.type === 'start');
                  if (!startNode) return <Typography variant="body2" color="text.secondary">{t('processes:processDesigner.wizard.publish.noStartNode')}</Typography>;

                  // BFS render with indent
                  const rendered: { node: typeof flowNodes[0]; depth: number; label?: string }[] = [];
                  const visited = new Set<string>();
                  const queue: { id: string; depth: number; label?: string }[] = [{ id: (startNode as any).id ?? 'n_start', depth: 0 }];
                  while (queue.length > 0) {
                    const { id, depth, label } = queue.shift()!;
                    if (visited.has(id)) continue;
                    visited.add(id);
                    const node = flowNodes.find((n: any) => n.id === id || (n.type === 'start' && id.includes('start')));
                    if (node) rendered.push({ node, depth, label });
                    const children = childrenOf.get(id) ?? [];
                    const childEdges = edges.filter((e: any) => e.source === id);
                    children.forEach((cid: string) => {
                      const edge = childEdges.find((e: any) => e.target === cid);
                      queue.push({ id: cid, depth: depth + (children.length > 1 ? 1 : 0), label: edge?.label });
                    });
                  }

                  return (
                    <Stack spacing={0.5}>
                      {rendered.map((item, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: item.depth * 3 }}>
                          {item.depth > 0 && <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>└</Typography>}
                          <Box sx={{
                            width: 10, height: 10, borderRadius: item.node.type === 'condition' ? '2px' : '50%', flexShrink: 0,
                            bgcolor: item.node.type === 'start' ? 'success.main' : item.node.type === 'end' ? 'grey.600' : item.node.type === 'condition' ? 'warning.main' : 'primary.main',
                            transform: item.node.type === 'condition' ? 'rotate(45deg)' : 'none',
                          }} />
                          {item.label && <Chip label={item.label} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'warning.100' }} />}
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{getProcessDefinitionNodeLabel(t, definition, item.node) || '—'}</Typography>
                          <Chip label={item.node.type ? t(`processes:flowEditor.nodeSpecs.${item.node.type}`) : t('processes:processDesigner.wizard.publish.nodeFallback')} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', ml: 'auto' }} />
                        </Box>
                      ))}
                    </Stack>
                  );
                })()}
              </Box>

              {/* The bulleted "Form fields" list that used to sit here is gone
                  (owner 2026-08-23: "最后的步骤 view 中是否有重复的显示？").
                  It restated what "What the requester sees" above already draws —
                  same labels, same required markers, same types — and it read its
                  labels through a SECOND path (`getProcessDefinitionFieldLabel`)
                  than the preview does, so the two could disagree about one string
                  (svdy-rules §10.5b). The COUNT in the stat row stays: how many
                  fields there are is a different fact from what they are. */}

              {/* Readiness checklist */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('processes:processDesigner.wizard.publish.readinessChecklist')}</Typography>
              <Stack spacing={0.75} sx={{ mb: 2 }}>
                {[
                  { ok: !!definition.name, label: t('processes:processDesigner.wizard.publish.checkName') },
                  { ok: hasStart, label: t('processes:processDesigner.wizard.publish.checkStart') },
                  { ok: hasEnd, label: t('processes:processDesigner.wizard.publish.checkEnd') },
                  { ok: formComponents.length > 0, label: t('processes:processDesigner.wizard.publish.checkForm'), optional: true },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* MUI ICONS, not glyphs. `✓` and `✗` are typographic and render in the text
                        colour; `⚠` (U+26A0) has EMOJI presentation by default, so one of the three
                        states was a coloured picture and the other two were letters — the row read
                        as two conventions (owner 2026-09-01: 不要搞这种icon). All three now come from
                        the same family at the same size, coloured by the same palette token the
                        label already uses. */}
                    {item.ok ? (
                      <CheckCircleOutlined fontSize="small" sx={{ color: 'success.main' }} />
                    ) : item.optional ? (
                      <ErrorOutlineOutlined fontSize="small" sx={{ color: 'warning.main' }} />
                    ) : (
                      <CancelOutlined fontSize="small" sx={{ color: 'error.main' }} />
                    )}
                    <Typography variant="body2" sx={{ color: item.ok ? 'success.main' : item.optional ? 'warning.main' : 'error.main' }}>
                      {item.label}{item.optional && !item.ok ? ` ${t('processes:processDesigner.wizard.publish.optional')}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {requiresApproverPreflight && definition.status === 'draft' && (
                <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: preflight?.ready ? 'success.main' : 'divider', borderRadius: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {t('processes:processDesigner.wizard.publish.approverPreflightTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {preflight?.ready
                          ? t('processes:processDesigner.wizard.publish.approverPreflightReady')
                          : t('processes:processDesigner.wizard.publish.approverPreflightDescription')}
                      </Typography>
                    </Box>
                    <Button
                      variant={preflight?.ready ? 'outlined' : 'contained'}
                      size="small"
                      onClick={handlePreflight}
                      disabled={preflighting}
                      sx={{ textTransform: 'none', borderRadius: 1 }}
                    >
                      {preflighting
                        ? <CircularProgress size={16} color="inherit" />
                        : t('processes:processDesigner.wizard.publish.checkApproverRoute')}
                    </Button>
                  </Stack>
                  {preflight?.ready && (
                    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                      {preflight.approval_steps.map((step) => (
                        <Typography key={step.node_id} variant="body2" color="success.main">
                          {t('processes:processDesigner.wizard.publish.approverStepReady', {
                            step: step.label,
                            approvers: step.approvers.map((worker) => worker.name).join(', '),
                          })}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {definition.status !== 'draft' && (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {t('processes:processDesigner.wizard.publish.alreadyPublished', { status: translateProcessStatus(t, definition.status) })}
                  </Alert>
                  <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t('processes:processDesigner.wizard.publish.testRequestTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                      {t('processes:processDesigner.wizard.publish.testRequestDescription')}
                    </Typography>
                    {/* NO URL FIELD (owner, 2026-09-01: 成功以后的url是不全的，是不是直接不要有
                        这个url的限制，只有一个复制按钮就可以复制了).
                      *
                      * It printed `testRequestPath` — a RELATIVE path — while the copy button copied
                      * `${window.location.origin}${testRequestPath}`. Two values for one link, and
                      * the one on screen was the broken one: an admin who read it and typed it
                      * somewhere got a link that resolves nowhere.
                      *
                      * The path is not information the admin needs to READ. It is a thing to send to
                      * a worker, so the button is the whole feature — and with the field gone there
                      * is only one value, which is the absolute one. */}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCopyTestRequestLink}
                      sx={{ textTransform: 'none', borderRadius: 1 }}
                    >
                      {t('processes:processDesigner.wizard.publish.copyTestRequestLink')}
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          );
        })()}
      </Box>

      {/* DISCARD-OR-STAY, the way out that `requestStep` used to withhold.
          Leaving a dirty draft was refused outright — including via the header's back arrow — so a
          draft could not be abandoned. `fetchDef` re-derives every step from the saved definition, so
          discarding costs nothing but the unsaved edits the user just said they do not want. */}
      <ConfirmDialog
        open={pendingLeave !== null}
        title={t('processes:processDesigner.wizard.discardTitle')}
        description={t('processes:processDesigner.wizard.discardDesc')}
        confirmLabel={t('processes:processDesigner.wizard.discardConfirm')}
        onClose={() => setPendingLeave(null)}
        onConfirm={async () => {
          const target = pendingLeave;
          setPendingLeave(null);
          if (target === null) return;
          setDirty(false);
          await fetchDef();
          if (target === 'back') { onBack(); return; }
          if (target >= 0 && target < STEPS_KEYS.length) setActiveStep(target);
        }}
      />

      <Snackbar
        // Keyed per message AND self-scoped on close. Measured 2026-08-23: the save-failure
        // toast's auto-hide timer fired AFTER the follow-up "save first" reminder was set and
        // cleared it, so the reminder never appeared — the render log went
        // `toast=saveFailed` → `toast=null` with the reminder in between. A shared
        // `onClose={() => setToast(null)}` cannot tell which message is closing; this one can.
        key={toast?.id ?? 'none'}
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => dismissToast(toast?.id)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => dismissToast(toast?.id)} sx={{ width: '100%' }}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ===========================================================================
// List view (entry point)
// ===========================================================================

const STATUS_OPTIONS = ['all', 'draft', 'active', 'deprecated', 'archived'] as const;

/** The link a WORKER opens to submit a test request. Absolute, because it is meant to be sent.
 *
 * One function because there were two expressions: the copy button built an absolute URL and the
 * read-only field beside it built a relative path, so the admin read one value and pasted another
 * (owner, 2026-09-01). The field is gone and this is what remains — see svdy-rules §10.5. */
function workerTestLink(definitionId: string): string {
  return `${window.location.origin}/me/requests/new?definitionId=${encodeURIComponent(definitionId)}`;
}

export default function ProcessDesigner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>({ kind: 'list' });
  const [definitions, setDefinitions] = useState<ProcessDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [toast, setToast] = useState<{ message: string; severity: "success" | "error" | "info" } | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ProcessDefinition | null>(null);
  const [pendingArchive, setPendingArchive] = useState<ProcessDefinition | null>(null);
  /** A delete the backend refused, held so the dialog can offer archiving instead of a red toast. */
  const [deleteBlocked, setDeleteBlocked] = useState<
    { definition: ProcessDefinition; code: string; activeCount: number } | null
  >(null);
  const [definitionMenu, setDefinitionMenu] = useState<{ anchorEl: HTMLElement; definition: ProcessDefinition } | null>(null);
  const [versionActionId, setVersionActionId] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setDefinitions(await processesApi.processDefinitionsApi.list()); }
    catch (e) { setError(e instanceof ApiError ? translateApiError(e, t) : t('processes:processDesigner.loadError')); setDefinitions([]); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Catalog templates are available starters, not tenant configuration. Keep one
  // customer-only source for list rows, counts, filters, and the first-value zero state.
  const customerDefinitions = useMemo(
    () => definitions.filter(d => !(d as any).is_template),
    [definitions],
  );

  // Chained filter: status → text search
  const filtered = useMemo(() => {
    let result = customerDefinitions;
    if (statusFilter !== 'all') result = result.filter(d => d.status === statusFilter);
    const q = filter.trim().toLowerCase();
    if (q) result = result.filter(d => {
      const display = getProcessDefinitionDisplay(t, d);
      return d.name.toLowerCase().includes(q)
        || (d.description ?? '').toLowerCase().includes(q)
        || display.name.toLowerCase().includes(q)
        || display.description.toLowerCase().includes(q);
    });
    return result;
  }, [customerDefinitions, statusFilter, filter, t]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const payload: ProcessDefinitionCreate = { name: t('processes:processDesigner.untitledProcess'), description: null, flow: { nodes: [], edges: [] } };
      const created = await processesApi.processDefinitionsApi.create(payload);
      setDefinitions(prev => [created, ...prev]);
      setMode({ kind: 'wizard', defId: created.id });
    } catch (e) { setToast({ message: e instanceof ApiError ? translateApiError(e, t) : t('processes:processDesigner.createFailed'), severity: 'error' }); }
    finally { setCreating(false); }
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    try {
      await processesApi.processDefinitionsApi.delete(pendingDelete.id);
      setDefinitions(prev => prev.filter(d => d.id !== pendingDelete.id));
      setToast({ message: t('processes:processDesigner.deletedToast', { name: pendingDelete.name }), severity: "success" });
      setPendingDelete(null);
    } catch (e) {
      // A REFUSAL WITH A WAY OUT, not just a red toast (2026-08-31).
      //
      // The backend refuses deletion for three reasons and archiving is the answer to all
      // three — it stops new requests and keeps every past one readable. Until now the admin
      // got a toast and no next step, and the toast was WRONG: nine locales said "create a new
      // version instead", which does not remove anything, while the backend's own message said
      // archive. Two sources of truth for one remedy (svdy-rules §10.5b), and the one the user
      // read was the one that could not help them.
      //
      // So the dialog hands off to the archive dialog with the same subject still selected.
      const blockedCode = e instanceof ApiError && e.status === 409 ? apiErrorCode(e) : null;
      if (blockedCode && blockedCode.startsWith('process_definition_has_')) {
        const blockedDetail = (() => {
          const raw = (e as ApiError).detail as { detail?: unknown } | null;
          const inner = raw && typeof raw === 'object' && 'detail' in raw ? raw.detail : raw;
          const count = (inner as { active_instance_count?: unknown } | null)?.active_instance_count;
          return typeof count === 'number' ? count : 0;
        })();
        setDeleteBlocked({ definition: pendingDelete, code: blockedCode, activeCount: blockedDetail });
        setPendingDelete(null);
        return;
      }
      setToast({ message: e instanceof ApiError ? translateApiError(e, t) : t('processes:processDesigner.deleteFailed'), severity: 'error' });
      throw e;
    }
  };

  const handleCloneVersion = async (definition: ProcessDefinition) => {
    setDefinitionMenu(null);
    setVersionActionId(definition.id);
    try {
      const created = await processesApi.processDefinitionsApi.cloneVersion(definition.id);
      setDefinitions(prev => [created, ...prev]);
      setToast({ message: t('processes:processDesigner.versioning.created', { version: created.version }), severity: 'success' });
      setMode({ kind: 'wizard', defId: created.id });
    } catch (e) {
      setToast({ message: e instanceof ApiError ? translateApiError(e, t) : t('processes:processDesigner.versioning.createFailed'), severity: 'error' });
    } finally {
      setVersionActionId(null);
    }
  };

  const handleArchiveConfirm = async () => {
    if (!pendingArchive) return;
    setVersionActionId(pendingArchive.id);
    try {
      const archived = await processesApi.processDefinitionsApi.archive(pendingArchive.id);
      setDefinitions(prev => prev.map(definition => definition.id === archived.id ? archived : definition));
      setToast({ message: t('processes:processDesigner.versioning.archived', { name: archived.name }), severity: 'success' });
      setPendingArchive(null);
    } catch (e) {
      setToast({ message: e instanceof ApiError ? translateApiError(e, t) : t('processes:processDesigner.versioning.archiveFailed'), severity: 'error' });
      throw e;
    } finally {
      setVersionActionId(null);
    }
  };

  if (mode.kind === 'wizard') {
    return (
      <DesignerWizard
        definitionId={mode.defId}
        definitions={definitions}
        onCreateVersion={handleCloneVersion}
        onBack={() => { setMode({ kind: 'list' }); fetchAll(); }}
      />
    );
  }

  return (
    <Box sx={{ py: '12px' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={600}>{t('processes:designer.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('processes:designer.subtitle')}</Typography>
        </Box>
        <Tooltip title={t('common:actions.refresh')}><IconButton onClick={fetchAll}><RefreshIcon /></IconButton></Tooltip>
        <Button
          variant="outlined"
          onClick={() => setCatalogOpen(true)}
          sx={{ textTransform: 'none', borderRadius: 1 }}
        >
          {t('processes:templateCatalog.open')}
        </Button>
        <Button variant="contained" startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />} onClick={handleCreate} disabled={creating}>{t('processes:processDesigner.newProcess')}</Button>
      </Stack>

      {/* Filter bar */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('processes:processDesigner.filterStatus')}</InputLabel>
          <Select value={statusFilter} label={t('processes:processDesigner.filterStatus')} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{t(`processes:processDesigner.statusOptions.${s}`)}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" placeholder={t('processes:processDesigner.searchPlaceholder')} value={filter} onChange={e => setFilter(e.target.value)} sx={{ flex: 1 }} />
        <Box sx={{ flex: 1 }} />
        {!loading && !error && (
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            {t('processes:processDesigner.definitionsCount', { shown: filtered.length, total: customerDefinitions.length })}
          </Typography>
        )}
      </Stack>

      {/* Active filter chips */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (statusFilter !== 'all') chips.push({ key: 'status', label: t('processes:processDesigner.statusChip', { value: translateProcessStatus(t, statusFilter) }), onClear: () => setStatusFilter('all') });
        if (filter.trim()) chips.push({ key: 'search', label: t('processes:processDesigner.searchChip', { value: filter.trim() }), onClear: () => setFilter('') });
        if (chips.length === 0) return null;
        return (
          <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map(c => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear} variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small" onClick={() => { setStatusFilter('all'); setFilter(''); }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5, '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              {t('processes:processDesigner.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
       : error ? null
       : customerDefinitions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            width: '100%', p: 5,
            borderRadius: 2, border: '1px solid', borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('processes:processDesigner.noDefinitions')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('processes:processDesigner.noDefinitionsHint')}
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setCatalogOpen(true)}
            sx={{ mt: 2, textTransform: 'none', borderRadius: 1 }}
          >
            {t('processes:templateCatalog.open')}
          </Button>
        </Paper>
       )
       : filtered.length === 0 ? <Alert severity="info">{t('processes:processDesigner.noMatches')}</Alert>
       : (
        <Grid container spacing={2}>
          {filtered.map(d => {
            const isActive = d.status === 'active';
            const display = getProcessDefinitionDisplay(t, d);
            return (
              <Grid item xs={12} sm={6} md={4} key={d.id}>
                <Card variant="outlined" sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  // `relative` so the delete action can sit OVER the card's top-right corner
                  // instead of in a row of its own — see the note where it is rendered.
                  position: 'relative',
                  ...(isActive && { borderLeft: '3px solid', borderLeftColor: 'success.main' }),
                  transition: 'border-color 120ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }}>
                  {/* Delete, in the corner rather than in a bordered row containing nothing
                      else. Each card used to end with a full-width action bar whose only
                      occupant was this one icon — a divider, 8px of padding and a row of
                      chrome per card, on a page that is a grid of cards.

                      It stays OUTSIDE the CardActionArea below, which is why the row existed
                      in the first place: the whole card is a button, and a button inside a
                      button is invalid HTML. Absolute positioning keeps it out of that
                      subtree while putting it where the eye expects a per-card action. */}
                  {(d.status === 'draft' || d.status === 'archived') && (
                    <Tooltip title={t('common:actions.delete')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); setPendingDelete(d); }}
                        sx={{
                          position: 'absolute',
                          top: `${CARD_CONTENT_PAD}px`,
                          height: CARD_HEADER_ROW_H,
                          insetInlineEnd: `${CARD_CONTENT_PAD - 6}px`,
                          zIndex: 1,
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {d.status === 'active' && (
                    <Tooltip title={t('processes:processDesigner.versioning.cardActions')}>
                      <IconButton
                        size="small"
                        aria-label={t('processes:processDesigner.versioning.cardActions')}
                        disabled={versionActionId === d.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          setDefinitionMenu({ anchorEl: event.currentTarget, definition: d });
                        }}
                        sx={{
                          position: 'absolute',
                          top: `${CARD_CONTENT_PAD}px`,
                          height: CARD_HEADER_ROW_H,
                          insetInlineEnd: `${CARD_CONTENT_PAD - 6}px`,
                          zIndex: 1,
                        }}
                      >
                        {versionActionId === d.id ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* TOP-aligned, explicitly. With `flex: 1` alone the body ended up vertically
                      CENTRED inside the stretched card, so the three titles in a row sat at
                      three different heights — lowest on the card with the least content
                      ("Untitled Process", which has no description). Measured off the
                      screenshot: content box 44→262, so a 144px body centred starts at 81 and a
                      68px body at 119, which is exactly the 88 / 126 observed.
                      svdy-ui §15/§11.3: cards in one row share their first content row's
                      baseline, whatever they contain. Written out rather than relying on a
                      default, because the default is what produced this. */}
                  <CardActionArea
                    onClick={() => setMode({ kind: 'wizard', defId: d.id })}
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {/* `paddingInlineEnd` for the corner button's clearance — not `pr`, which
                        the RTL plugin flips to padding-LEFT and throws onto the wrong side
                        (svdy-ui §17). */}
                    <CardContent sx={{ p: 2 }}>
                      {/* `minHeight` from the shared constant so the delete button over it
                          shares this row's centre line, and `paddingInlineEnd` (not `pr` — the
                          RTL plugin flips `pr` to the wrong side, svdy-ui §17) to keep the
                          status chip clear of the button. */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ minHeight: `${CARD_HEADER_ROW_H}px`, paddingInlineEnd: '30px' }}
                      >
                        <FlowIcon color="primary" fontSize="small" />
                        {isActive && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />}
                        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, fontSize: '0.95rem' }} noWrap>{display.name}</Typography>
                        <Chip label={translateProcessStatus(t, d.status)} size="small" color={STATUS_COLORS[d.status] ?? 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Stack>
                      {display.description && (
                        <Typography variant="caption" color="text.secondary" sx={{
                          mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4,
                        }}>{display.description}</Typography>
                      )}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5, gap: 0.5 }}>
                        <Chip label={`v${d.version}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        <Chip label={t('processes:processDesigner.nodes', { count: ((d.flow as any)?.nodes?.length ?? 0) })} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Menu
        anchorEl={definitionMenu?.anchorEl ?? null}
        open={!!definitionMenu}
        onClose={() => setDefinitionMenu(null)}
        onClick={(event) => event.stopPropagation()}
        PaperProps={{ sx: { minWidth: 300, maxWidth: 360 } }}
      >
        <MenuItem
          disabled={!definitionMenu || versionActionId === definitionMenu.definition.id}
          onClick={() => definitionMenu && handleCloneVersion(definitionMenu.definition)}
        >
          <ListItemIcon><VersionIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary={t('processes:processDesigner.versioning.create')}
            secondary={t('processes:processDesigner.versioning.createDescription')}
            secondaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}
          />
        </MenuItem>
        <Divider />
        <MenuItem
          disabled={!definitionMenu || versionActionId === definitionMenu.definition.id}
          onClick={() => {
            if (!definitionMenu) return;
            setPendingArchive(definitionMenu.definition);
            setDefinitionMenu(null);
          }}
          sx={{ color: 'warning.dark' }}
        >
          <ListItemIcon><ArchiveIcon fontSize="small" color="warning" /></ListItemIcon>
          <ListItemText primary={t('processes:processDesigner.versioning.archive')} />
        </MenuItem>
        <MenuItem
          disabled={!definitionMenu || versionActionId === definitionMenu.definition.id}
          onClick={() => {
            if (!definitionMenu) return;
            setPendingDelete(definitionMenu.definition);
            setDefinitionMenu(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText
            primary={t('common:actions.delete')}
            // Offered for real: the backend allows it only when nothing references the
            // definition, and answers a used one with a 409 naming Archive instead. Hiding the
            // action entirely was what made a mistakenly published process permanent.
            secondary={t('processes:processDesigner.deleteOnlyWithoutHistory')}
            secondaryTypographyProps={{ sx: { whiteSpace: 'normal' } }}
          />
        </MenuItem>
      </Menu>

      <TemplateCatalogDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onAdded={(draftId) => {
          setCatalogOpen(false);
          fetchAll();
          setMode({ kind: 'wizard', defId: draftId });
        }}
      />

      <ConfirmDialog open={!!pendingDelete} title={t('processes:processDesigner.deleteTitle')} description={t('processes:processDesigner.deleteDesc')}        subject={pendingDelete ? <Typography fontWeight={600}>{pendingDelete.name}</Typography> : null}
        confirmLabel={t('common:actions.delete')} tone="danger" onClose={() => setPendingDelete(null)} onConfirm={handleDeleteConfirm} />

      {/* THE REFUSAL, WITH THE WAY OUT ON IT (2026-08-31).
        * Archiving is the answer to all three refusal codes, so the confirm button IS archive
        * rather than a dismissal — the admin does not have to find a different menu item to do
        * the thing we just told them to do. `description` is keyed per code, because
        * "approval history", "an uploaded file" and "another process started sub-requests from
        * this one" are three different facts about their data and only one of them was ever
        * being reported.
        */}
      {/* THE REMEDY DEPENDS ON THE REASON (owner, 2026-09-01).
        *
        * A recorded decision is permanent, so the only move is to archive. Requests still going
        * through approval are TEMPORARY and, more to the point, FINDABLE — so the button takes the
        * admin to them, filtered to this process, instead of offering an archive they may not want.
        * Offering "Archive" for a live-request refusal invites the wrong action: archiving stops
        * new requests but does nothing about the ones blocking the delete. */}
      <ConfirmDialog
        open={!!deleteBlocked}
        title={t('processes:processDesigner.deleteBlocked.title')}
        description={deleteBlocked
          ? t(`processes:processDesigner.deleteBlocked.${deleteBlocked.code}`, {
              count: deleteBlocked.activeCount,
            })
          : ''}
        subject={deleteBlocked
          ? <Typography fontWeight={600}>{getProcessDefinitionDisplay(t, deleteBlocked.definition).name}</Typography>
          : null}
        confirmLabel={deleteBlocked?.code === 'process_definition_has_active_requests'
          ? t('processes:processDesigner.deleteBlocked.viewRequests')
          : t('processes:processDesigner.versioning.archive')}
        cancelLabel={t('processes:processDesigner.deleteBlocked.keepIt')}
        // SEE THE REQUESTS — on EVERY refusal, not only the live-requests one. Being told
        // "someone already approved a request on this process" is only actionable if you can look
        // at those requests; same for attachments and sub-flows, since all four refusals are ABOUT
        // requests and the admin's next question is always "which ones". A third action rather than
        // replacing Archive: for a decided process archiving is still the remedy, so the dialog
        // offers the remedy AND the evidence.
        extraAction={deleteBlocked && deleteBlocked.code !== 'process_definition_has_active_requests'
          ? {
            label: t('processes:processDesigner.deleteBlocked.viewRequests'),
            onClick: () => {
              const id = deleteBlocked.definition.id;
              setDeleteBlocked(null);
              navigate(`/processes/requests/history?definitionId=${encodeURIComponent(id)}`);
            },
          }
          : undefined}
        onClose={() => setDeleteBlocked(null)}
        onConfirm={async () => {
          const blocked = deleteBlocked;
          if (!blocked) return;
          setDeleteBlocked(null);
          if (blocked.code === 'process_definition_has_active_requests') {
            // Live requests: seeing them IS the remedy, so it is the primary action and the list
            // opens pre-filtered to the ones still in flight.
            navigate(
              `/processes/requests/history?definitionId=${encodeURIComponent(blocked.definition.id)}`
              + '&status=active',
            );
            return;
          }
          setPendingArchive(blocked.definition);
        }}
      />

      <ConfirmDialog
        open={!!pendingArchive}        title={t('processes:processDesigner.versioning.archiveTitle', { name: pendingArchive?.name ?? '' })}
        description={t('processes:processDesigner.versioning.archiveDescription')}
        subject={pendingArchive ? <Typography fontWeight={600}>{pendingArchive.name}</Typography> : null}
        confirmLabel={t('processes:processDesigner.versioning.archive')}
        tone="danger"
        onClose={() => setPendingArchive(null)}
        onConfirm={handleArchiveConfirm}
      />

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.message}</Alert></Snackbar>
    </Box>
  );
}
